import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const testicularBiopsy: ContentBlock<"procedure"> = {
  id: makeId("proc-testicular-biopsy"),
  kind: "procedure",
  tags: ["testing", "his", "clinic-visit"],
  title: "Testicular biopsy",
  subtitle: "Tissue sample from the testicle for diagnosis or sperm retrieval",
  body: {
    procedureName: "Testicular biopsy",
    category: "diagnostic",
    description:
      "A procedure where a small sample of tissue is taken from one or both testicles to evaluate sperm production, diagnose causes of male infertility, or retrieve sperm for use in IVF/ICSI.",
    howItWorks:
      "The procedure is performed under local anesthesia with or without sedation. A small incision is made in the scrotum and a tiny piece of testicular tissue is removed using a needle or a small surgical cut. The tissue sample is examined under a microscope by a pathologist to assess whether the testicle is producing sperm normally. If sperm are found, they may be frozen for future fertility treatment. The incision is closed with dissolvable stitches.",
    duration: "15–30 minutes",
    anesthesia: "local" as const,
    recoveryTime: "2–3 days of rest; avoid strenuous activity for 1–2 weeks",
    whatToExpect: [
      "Performed as an outpatient procedure, and you go home the same day",
      "Mild soreness and swelling at the biopsy site for several days",
      "Ice packs and over-the-counter pain relief help manage discomfort",
      "Pathology results take about 1–2 weeks",
      "If sperm are found, they can be cryopreserved for future IVF/ICSI",
    ],
    results: [
      {
        label: "Pathology",
        detail:
          "Pathology results usually take about 1-2 weeks and describe sperm production in the sampled tissue.",
      },
      {
        label: "Sperm found",
        detail:
          "If sperm are found, the lab may freeze them for future IVF/ICSI or coordinate use with an active cycle.",
      },
      {
        label: "If none are found",
        detail:
          "Your doctor should review whether another retrieval approach, donor sperm, or other options make sense.",
      },
    ],
    prepInstructions: [
      "Follow your doctor's instructions about eating and drinking before the procedure",
      "Arrange for someone to drive you home",
      "Wear supportive, loose-fitting underwear",
      "Inform your doctor about blood-thinning medications or supplements",
    ],
    risks: [
      "Mild pain, swelling, or bruising at the biopsy site",
      "Rare: infection or bleeding",
      "Very rare: persistent discomfort or hematoma",
    ],
    forWhom: "him",
    frBody: {
      procedureName: "Biopsie testiculaire",
      description:
        "Prélèvement d'un petit échantillon de tissu testiculaire pour évaluer la spermatogenèse, diagnostiquer une cause d'infertilité masculine ou recueillir des spermatozoïdes en vue d'une FIV (fécondation in vitro) ou d'une ICSI (injection intracytoplasmique de spermatozoïde).",
      howItWorks:
        "Le geste est réalisé sous anesthésie locale, avec ou sans sédation. Une petite incision est faite au niveau du scrotum et un petit fragment de tissu testiculaire est prélevé à l'aiguille ou par une mini-incision chirurgicale. L'échantillon est examiné au microscope par un anatomopathologiste pour apprécier la production de spermatozoïdes. Si des spermatozoïdes sont retrouvés, ils peuvent être congelés en vue d'un traitement ultérieur. L'incision est fermée par des points résorbables.",
      duration: "15 à 30 minutes",
      recoveryTime:
        "2 à 3 jours de repos ; éviter les efforts intenses pendant 1 à 2 semaines",
      whatToExpect: [
        "Réalisée en ambulatoire, vous rentrez chez vous le jour même",
        "Sensibilité et léger gonflement au site de biopsie pendant plusieurs jours",
        "La glace et des antalgiques en vente libre aident à gérer l'inconfort",
        "Résultats anatomopathologiques sous 1 à 2 semaines",
        "Si des spermatozoïdes sont retrouvés, ils peuvent être cryopréservés pour une FIV/ICSI ultérieure",
      ],
      results: [
        {
          label: "Anatomopathologie",
          detail:
            "Les résultats reviennent généralement sous 1 à 2 semaines et décrivent la production de spermatozoïdes dans le tissu analysé.",
        },
        {
          label: "Spermatozoïdes retrouvés",
          detail:
            "Si des spermatozoïdes sont retrouvés, le laboratoire peut les congeler pour une FIV/ICSI ultérieure ou coordonner leur utilisation avec un cycle en cours.",
        },
        {
          label: "Si aucun n'est retrouvé",
          detail:
            "Votre médecin doit revoir si une autre technique de recueil, un don de sperme ou d'autres options sont pertinentes.",
        },
      ],
      prepInstructions: [
        "Suivez les consignes alimentaires et de boisson données par votre médecin",
        "Prévoyez quelqu'un pour vous raccompagner",
        "Portez des sous-vêtements soutenants et amples",
        "Signalez à votre médecin tout anticoagulant ou complément susceptible d'influencer la coagulation",
      ],
      risks: [
        "Douleur, gonflement ou ecchymose légers au site de biopsie",
        "Rare : infection ou saignement",
        "Très rare : inconfort persistant ou hématome",
      ],
    },
  },
  sources: [
    {
      title:
        "Diagnosis and Treatment of Infertility in Men: AUA/ASRM Guideline",
      authors: "Schlegel PN, Sigman M, Collura B, et al.",
      journal: "Journal of Urology",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/diagnosis-and-treatment-of-infertility-in-men-auaasrm-guideline-part-i-2020/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(testicularBiopsy);
